import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deleteClient } from "../../Redux/Thunks/clientsThunk";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import {
  RiDeleteBinFill,
  RiPencilLine,
  RiEyeLine,
  RiFileList3Line,
  RiCloseLine,
} from "react-icons/ri";
import { ContentLoader } from "../../Utility/Loader";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import ListPaginationBar from "../../Utility/Pagination/ListPaginationBar";
import ClientFilter from "./ClientFilter";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FloatingTooltip from "../../Utility/FloatingTooltip/FloatingTooltip";
import ClientHoursSummaryModal from "./ClientHoursSummaryModal";
import DeleteConfirmModal from "../../Utility/DeleteConfirmModal/DeleteConfirmModal";
import { useSorting, SortableHeader } from "../../Utility/Sorting";
import { canDeleteClient } from "../../constants/clientOnHoldRules";
import { mapClientToTableRow, CLIENTS_LIST_PAGE_SIZE } from "./clientsDataUtils";
import "./clients.css";
import { usePermissions } from "../../hooks/usePermissions";
import PermissionDeniedModal from "../../Utility/PermissionDeniedModal/PermissionDeniedModal";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";

const ClientList = ({
  clientsData,
  projectsData,
  loading,
  searchTerm,
  currentPage,
  setCurrentPage,
  limit = CLIENTS_LIST_PAGE_SIZE,
  onPageSizeChange,
  listingFilter,
  /** Same query as GET /clients (page/limit/filters) for refetch after add/edit */
  clientsFetchParams,
  handleFilterChange,
  handleFilterReset,
  applyFilters,
  onRefetchClients,
  onClientClick,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoursSummaryClient, setHoursSummaryClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deniedPermissionKey, setDeniedPermissionKey] = useState(null);
  const { can } = usePermissions();

  const canViewClientDashboard = can(PERMISSION_KEYS.CLIENTS_DASHBOARD_VIEW);
  const canEditClient = can(PERMISSION_KEYS.CLIENTS_EDIT);
  const canDeleteClientPerm = can(PERMISSION_KEYS.CLIENTS_DELETE);
  const showActionsColumn = canViewClientDashboard || canEditClient || canDeleteClientPerm;
  const colCount = showActionsColumn ? 4 : 3;

  const list = clientsData?.list ?? [];
  const meta = clientsData?.meta ?? null;
  const totalCount = meta?.totalCount ?? meta?.total ?? list.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / (limit || CLIENTS_LIST_PAGE_SIZE)));

  // Optional client-side sort on current page
  const { sortedData, sortKey, sortDirection, handleSort } = useSorting(list, {
    defaultSortKey: null,
    defaultSortDirection: "asc",
  });

  const paginatedClients = sortedData;

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDeleteClick = (client) => {
    if (!can(PERMISSION_KEYS.CLIENTS_DELETE)) {
      setDeniedPermissionKey(PERMISSION_KEYS.CLIENTS_DELETE);
      return;
    }
    setClientToDelete(client);
  };

  const hasActiveProjects = clientToDelete
    ? !canDeleteClient(clientToDelete, projectsData?.list)
    : false;

  const handleConfirmDelete = async () => {
    if (!clientToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteClient(clientToDelete.id)).unwrap();
      if (onRefetchClients) onRefetchClients();
      else
        dispatch(
          fetchClientsData(
            clientsFetchParams ?? { page: 1, limit: limit || CLIENTS_LIST_PAGE_SIZE }
          )
        );
      setClientToDelete(null);
    } catch {
      // Keep modal open on error; could show error message
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="clients-page-content !bg-transparent">
      <div className="w-full ">
        <ContentLoader loading={loading} className="min-h-[calc(100vh-12rem)]">
          {/* Filter Section - always show so user can type name, status, contact person, click Filter */}
          <div className="">
            <ClientFilter
              listingFilter={listingFilter}
              handleFilterChange={handleFilterChange}
              handleFilterReset={handleFilterReset}
              applyFilters={applyFilters}
            />
          </div>

          {/* Table Card */}
          <div>
            <div className="w-full">
              <div className="relative flex flex-col min-w-0 bg-surface-primary  shadow-sm transition-shadow mt-4">
                <div className="flex-1 p-2">
                  <div className="live-preview">
                    <div className="overflow-x-auto">
                      <table className="w-full  mb-0 clients-table">
                        <thead className="bg-surface-secondary">
                          <tr>
                            <SortableHeader
                              columnKey="clientName"
                              label="Client Name"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="contactPerson"
                              label="Contact Person"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                            />
                            <SortableHeader
                              columnKey="status"
                              label="Status"
                              onSort={handleSort}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              sortable={false}
                            />
                            {showActionsColumn ? (
                              <SortableHeader
                                columnKey="action"
                                label="Action"
                                onSort={handleSort}
                                currentSortKey={sortKey}
                                sortDirection={sortDirection}
                                sortable={false}
                                className="action-col"
                              />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody className="relative">
                          {loading ? (
                            <tr aria-hidden="true">
                              <td colSpan={colCount} className="min-h-[240px] h-60 p-0 border-0" />
                            </tr>
                          ) : (
                            <>
                              {paginatedClients.length > 0 ? (
                                paginatedClients.map((client) => {
                                  const row = mapClientToTableRow(client);
                                  const badgeClass =
                                    row.statusDisplay.badgeVariant === "success"
                                      ? "bg-badge-success"
                                      : row.statusDisplay.badgeVariant === "danger"
                                        ? "bg-badge-danger"
                                        : row.statusDisplay.badgeVariant === "warning"
                                          ? "bg-badge-warning"
                                          : "bg-badge-secondary";
                                  return (
                                    <tr key={client.id}>
                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          onClick={
                                            canViewClientDashboard && onClientClick
                                              ? () => onClientClick(client)
                                              : undefined
                                          }
                                          className={
                                            canViewClientDashboard && onClientClick
                                              ? "cursor-pointer underline hover:opacity-80 text-text-link"
                                              : ""
                                          }
                                        >
                                          {row.clientName}
                                        </Typography>
                                      </td>

                                      <td className="text-start align-middle ">
                                        <Typography variant="span1">{row.contactPerson}</Typography>
                                      </td>
                                      <td className="text-start align-middle">
                                        <Typography
                                          variant="span1"
                                          className={`badge rounded-full px-3 py-1 text-white text-xs font-bold ${badgeClass}`}
                                        >
                                          {row.statusDisplay.text}
                                        </Typography>
                                      </td>

                                      {showActionsColumn ? (
                                        <td className="text-start align-middle content-cell">
                                          <div className="flex gap-0.5 items-center justify-end gap-2">
                                            {canViewClientDashboard ? (
                                              <FloatingTooltip label="View Dashboard">
                                                <Button
                                                  type="button"
                                                  onClick={() =>
                                                    onClientClick && onClientClick(client)
                                                  }
                                                  aria-label="View Dashboard"
                                                  size="sm"
                                                  className="btn-info-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiEyeLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canEditClient ? (
                                              <FloatingTooltip label="Edit">
                                                <Button
                                                  onClick={() =>
                                                    navigate(`/clients/edit/${client.id}`, {
                                                      state: {
                                                        previousPath:
                                                          location.pathname +
                                                          (location.search || ""),
                                                        ...(clientsFetchParams
                                                          ? { clientsFetchParams }
                                                          : {}),
                                                      },
                                                    })
                                                  }
                                                  aria-label="Edit client"
                                                  size="sm"
                                                  className="
                                        btn-warning-new
                                        !w-7 !h-7 
                                        !p-0 
                                        !font-normal 
                                        !leading-none
                                        flex items-center justify-center
                                        border-0
                                      "
                                                >
                                                  <RiPencilLine className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canEditClient ? (
                                              <FloatingTooltip label="Hours summary (Email / Export)">
                                                <Button
                                                  type="button"
                                                  onClick={() => setHoursSummaryClient(client)}
                                                  aria-label="Hours summary (Email / Export)"
                                                  size="sm"
                                                  className="!w-7 !h-7 !p-0 flex items-center justify-center border-0 bg-gray-600 hover:bg-gray-700 text-white"
                                                >
                                                  <RiFileList3Line className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}

                                            {canDeleteClientPerm ? (
                                              <FloatingTooltip label="Delete">
                                                <Button
                                                  onClick={() => handleDeleteClick(client)}
                                                  aria-label="Delete client"
                                                  size="sm"
                                                  className="btn-danger-new !w-7 !h-7 !p-0 flex items-center justify-center hover:opacity-90 border-0"
                                                >
                                                  <RiDeleteBinFill className="text-white text-[12px]" />
                                                </Button>
                                              </FloatingTooltip>
                                            ) : null}
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={colCount} className="text-center">
                                    <NoDataFound
                                      message={
                                        searchTerm
                                          ? "No clients found matching your search"
                                          : "No clients available"
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center mt-2 text-center sm:text-start"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {list.length > 0 && (
            <ListPaginationBar
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </ContentLoader>
      </div>

      <ClientHoursSummaryModal
        isOpen={!!hoursSummaryClient}
        onClose={() => setHoursSummaryClient(null)}
        client={hoursSummaryClient}
      />

      {/* Warning modal: client has active projects — cannot delete */}
      {clientToDelete && hasActiveProjects && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
          onClick={() => setClientToDelete(null)}
        >
          <div
            className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border-primary">
              <Typography variant="h5" className="font-semibold text-text-primary">
                Cannot delete client
              </Typography>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setClientToDelete(null)}
                className="!border-0 !shadow-none !bg-transparent !p-0 !min-w-0"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5 text-text-secondary" />
              </Button>
            </div>
            <div className="p-4">
              <Typography variant="text2" className="text-text-primary">
                This client (<strong>{clientToDelete?.clientName}</strong>) has project(s) in In
                Progress status. Complete or cancel those projects before deleting the client.
              </Typography>
              <div className="flex justify-end mt-6">
                <Button variant="primary" size="md" onClick={() => setClientToDelete(null)}>
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation: only when client has no active projects */}
      <DeleteConfirmModal
        isOpen={!!clientToDelete && !hasActiveProjects}
        onClose={() => setClientToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete client"
        itemName={clientToDelete?.clientName}
        loading={deleteLoading}
      />
      <PermissionDeniedModal
        isOpen={deniedPermissionKey != null}
        onClose={() => setDeniedPermissionKey(null)}
        permissionKey={deniedPermissionKey}
      />
    </div>
  );
};

export default ClientList;
